import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";

export type ResolvedTarget = {
	// The authoritative target star band.
	targetStars: number;
	// The admin's chosen point goal BEFORE the safety buffer — the value the Set Target dialog
	// edits and the band-derived target. Never inflated by the buffer.
	baseTargetPoints: number;
	// The flat safety-margin points added on top of the base (projectedScoreBufferPct). Explicit
	// so callers never silently drop it.
	bufferPoints: number;
	// The effective goal the plan is measured against: base + buffer. This is the number the gauge
	// target arc, the progress-meter tick, the gap callout, and `reachesTarget` all use, so the
	// hero and the "How" column never disagree.
	targetPoints: number;
};

/**
 * THE single source of truth for the Prediction tab's target points. Both the hero
 * (PredictionHero) and the scoring/plan-outcome path (ScoringAndRecommendations) resolve their
 * target through here so the gauge, progress meter, verdict, and `reachesTarget` always agree.
 *
 * Semantics decided once here:
 *  - STARS: `draft.stars` is authoritative (it reflects live Set-Target edits before a recompute);
 *    the `?? prediction.targetStars` only guards the nullable signature — the page always supplies a
 *    draft, and at rest `draft.stars` already equals `prediction.targetStars`, so it's a superset,
 *    not a behavior change.
 *  - BASE POINTS: the admin's explicit `targetPoints` (draft → server-resolved config) wins;
 *    otherwise the lower bound of the target star band.
 *  - BUFFER: `projectedScoreBufferPct` is a FLAT point buffer (legacy name) that the BE greedy adds
 *    to its stop condition. It is therefore part of the effective TARGET the plan must clear — so
 *    `targetPoints = base + buffer`. Read directly (the field is non-nullable `number`); no `?? 0`.
 *    The base is kept separate so the Set Target dialog edits the un-buffered goal.
 */
export const resolveTargetPoints = (prediction: PbjPrediction, draft: PbjModelUpsertBody | null): ResolvedTarget => {
	const targetStars = draft?.stars ?? prediction.targetStars;

	const explicitTarget = draft?.targetPoints ?? prediction.config.targetPoints;
	// DEFAULT: the star-band lower bound is the fallback when no explicit target is set. The final
	// `?? maxPoints` only fires if the BE omitted the band for `targetStars` (shouldn't happen for a
	// valid 1–5 rating) — max is the conservative choice so we never falsely report "target reached".
	const baseTargetPoints = explicitTarget ?? prediction.starThresholds.find((th) => th.star === targetStars)?.min ?? prediction.maxPoints;

	const bufferPoints = prediction.config.projectedScoreBufferPct;

	return {
		targetStars,
		baseTargetPoints,
		bufferPoints,
		targetPoints: baseTargetPoints + bufferPoints,
	};
};
